import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { requestPasswordReset, error, user, isLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // countdown effect
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    try {
      await requestPasswordReset(email);
      setSuccessMessage(
        "Password reset instructions have been sent. Please check your inbox."
      );
      setCooldown(60); // start 1 minute cooldown
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return; // block if still on cooldown
    await handleSubmit(); // reuse same logic
  };

  if (!isLoading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      className="h-screen w-full flex items-center justify-center"
      style={{
        background: "linear-gradient(to bottom left, #413072, #08080B)",
      }}
    >
      {!successMessage ? (
        // --- Before email sent ---
        <div className="bg-white max-w-[600px] w-full space-y-6 p-6 md:p-8 shadow-[0px_0px_20px_#000a] rounded-2xl max-md:mx-5">
          <button
            className="bg-transparent hover:bg-transparent text-[#007A78] flex items-center !p-0"
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon className="w-5 h-5" /> Back
          </button>

          <div>
            <h2 className="!mt-4 text-3xl font-extrabold text-gray-900">
              Forgot Password
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter your account email and we’ll send reset instructions.
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email-address"
                className="!text-left block text-sm mb-1"
              >
                Email
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full bg-[#f3f4f8] border-none rounded-[10px] shadow-[0_1.5px_1px_#c1c7cd] text-sm mt-1 mb-2.5 py-[1em] px-[1.25em]"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full relative overflow-hidden py-3 font-bold text-base bg-[#007A78] rounded-lg text-white"
              >
                {isSubmitting ? "Sending..." : "Send Reset Instructions"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        // --- After email sent ---
        <div className="bg-white max-w-[600px] w-full space-y-6 p-6 md:p-8 shadow-[0px_0px_20px_#000a] rounded-2xl max-md:mx-5">
          <button
            className="bg-transparent hover:bg-transparent text-[#007A78] flex items-center !p-0"
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon className="w-5 h-5" /> Back
          </button>

          <div>
            <h2 className="!mt-4 text-3xl font-extrabold text-gray-900">
              Email Sent
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Reset instructions have been sent to{" "}
              <span className="font-semibold">{email}</span>. Please check your
              inbox (and spam folder).
            </p>
          </div>

          <div className="mt-8 space-y-6">
            <button
              onClick={() => navigate("/login")}
              className="w-full relative overflow-hidden py-3 font-bold text-base bg-[#007A78] rounded-lg text-white"
            >
              Go Back to Login
            </button>
            <div className="text-sm text-[#302571] text-center !mt-3">
              Didn’t receive the email?{" "}
              <button
                onClick={handleResend}
                disabled={cooldown > 0}
                className="text-[#302571] font-bold !p-0 !bg-transparent disabled:opacity-50"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotPasswordPage;
